import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthService from '../../services/authService';
import { AuthContext } from '../../contexts/AuthContext';

const MFARecoveryRequest = () => {
  const { user, pendingAuth } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Use user from pendingAuth if available (during MFA flow), otherwise use authenticated user
  const currentUser = pendingAuth?.user || user;
  
  // Debug logging
  console.log('[MFA RECOVERY] user:', user);
  console.log('[MFA RECOVERY] pendingAuth:', pendingAuth);
  console.log('[MFA RECOVERY] currentUser:', currentUser);

  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If we have authenticated user, use that. Otherwise, require email input.
    if (!currentUser?.email && !email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    
    try {
      if (currentUser?.email) {
        // User is authenticated, use auth-based recovery
        await AuthService.requestMFARecovery();
      } else {
        // User is not authenticated, use email-based recovery
        await AuthService.requestMFARecoveryByEmail(email.trim());
      }
      setSubmitted(true);
      toast.success('Recovery email sent! Check your inbox for instructions.');
    } catch (error) {
      console.error('MFA recovery request failed:', error);
      toast.error('Unable to process recovery request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8"
        >
          <div className="text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-600" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
              Recovery Email Sent
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent recovery instructions to <strong>{currentUser?.email || email}</strong>
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Please check your email and follow the instructions to recover access to your account.
              The recovery link will expire in 24 hours.
            </p>
            
            <div className="mt-6">
              <a 
                href="/login" 
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Return to Login
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="text-center">
            <Shield className="mx-auto h-16 w-16 text-primary-600" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
              MFA Account Recovery
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Can't access your multi-factor authentication device? We'll send you a recovery link to regain access.
            </p>
          </div>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Account Info Display - show if user is authenticated */}
            {currentUser?.email ? (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-primary-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-primary-900">Recovery for account:</p>
                    <p className="text-sm text-primary-700">{currentUser.email}</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Email Input - show if user is not authenticated */
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Mail className="absolute right-3 top-2 h-5 w-5 text-gray-400" />
                </div>
              </div>
            )}

          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="text-sm text-blue-800">
              <strong>Security Notice:</strong> We'll send you a secure recovery link to help you regain access to your account.
              Recovery links expire in 24 hours and can only be used once.
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending Recovery Email...
                </div>
              ) : (
                'Send Recovery Email'
              )}
            </button>
          </div>

          <div className="text-center">
            <a 
              href="/login" 
              className="text-sm text-gray-600 hover:text-gray-500"
            >
              ← Back to Login
            </a>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default MFARecoveryRequest;
